'use client';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PropertyCard, { Property, formatAreaValue } from '@/components/PropertyCard';
import { useCurrency } from '@/context/CurrencyContext';
import AppImage from '@/components/ui/AppImage';

interface DbProperty {
  id: number;
  title: string;
  price?: string | number | null;
  area_size?: string | number | null;
  land_area?: string | number | null;
  status?: string | null;
  badges?: Array<{
    badge_show_on_property_images: boolean;
    badge_color: string;
    label_name: string;
  }>;
  featured_image?: string | null;
  slug?: string | null;
  type?: string | null;
  category?: string | null;
  pl_score_total?: number | null;
  price_label?: string | null;
  neighborhood?: string | null;
  description?: string | null;
}

interface CategoryListTemplate1Props {
  properties: DbProperty[];
  total: number;
  currentPage: number;
  totalPages: number;
  slug: string;
  pageTitle: string;
  pageDesc: string;
}

export default function CategoryListTemplate1({
  properties,
  total,
  currentPage,
  totalPages,
  slug,
  pageTitle,
  pageDesc,
}: CategoryListTemplate1Props) {
  const { formatPrice } = useCurrency();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<string>('default');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [quickViewProperty, setQuickViewProperty] = useState<Property | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);

  // Sync state from query parameters on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlSort = params.get('sort');
      const urlView = params.get('view');
      if (urlSort) setSortBy(urlSort);
      if (urlView === 'grid' || urlView === 'list') setViewMode(urlView);
    }
  }, []);

  // Handle outside click to close sort dropdown and modal click-outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (modalContainerRef.current && !modalContainerRef.current.contains(event.target as Node)) {
        setQuickViewProperty(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setQuickViewProperty(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const updateUrlParam = (key: string, value: string) => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.set(key, value);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState(null, '', newUrl);
    }
  };

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
    setIsDropdownOpen(false);
    updateUrlParam('sort', newSort);
  };

  const handleViewChange = (newView: 'grid' | 'list') => {
    setViewMode(newView);
    updateUrlParam('view', newView);
  };

  const getPageHref = (pageNum: number) => {
    const params = new URLSearchParams();
    params.set('page', pageNum.toString());
    if (sortBy !== 'default') params.set('sort', sortBy);
    if (viewMode !== 'grid') params.set('view', viewMode);
    return `/${slug}?${params.toString()}`;
  };

  const mappedProperties: Property[] = useMemo(() => {
    return properties.map((property: DbProperty) => ({
      id: property.id,
      title: property.title,
      price: property.price ? property.price.toString() : 'Price on Request',
      area: property.area_size ? `${property.area_size} sq.ft` : 'N/A',
      land_area: property.land_area ? property.land_area.toString() : undefined,
      gfa: property.area_size ? property.area_size.toString() : undefined,
      status: property.status || 'For Sale',
      label:
        property.badges && property.badges.length > 0 ? property.badges[0].label_name : 'Property',
      image: property.featured_image || '/uploads/placeholder.jpg',
      alt: property.title,
      href: `/property/${property.slug || property.id}`,
      type: property.type || property.category || 'RESIDENTIAL PLOTS FOR SALE',
      pl_score_total: property.pl_score_total || undefined,
      description: property.description || '',
      badges: property.badges || [],
    }));
  }, [properties]);

  const sortedProperties = useMemo(() => {
    const list = [...mappedProperties];
    if (sortBy === 'price-low') {
      list.sort((a, b) => {
        const valA = parseFloat(a.price.replace(/[^0-9.]/g, '')) || 0;
        const valB = parseFloat(b.price.replace(/[^0-9.]/g, '')) || 0;
        return valA - valB;
      });
    } else if (sortBy === 'price-high') {
      list.sort((a, b) => {
        const valA = parseFloat(a.price.replace(/[^0-9.]/g, '')) || 0;
        const valB = parseFloat(b.price.replace(/[^0-9.]/g, '')) || 0;
        return valB - valA;
      });
    } else if (sortBy === 'score-high') {
      list.sort((a, b) => {
        const getScore = (prop: Property) => {
          let score = prop.pl_score_total;
          if (score === undefined || score === null || score === 0) {
            const lowerTitle = prop.title.toLowerCase();
            if (lowerTitle.includes('meydan')) {
              score = 60;
            } else if (lowerTitle.includes('hotel plot')) {
              score = 90;
            } else if (lowerTitle.includes('villa plots')) {
              score = 85;
            } else if (lowerTitle.includes('jebel ali hills')) {
              score = 90;
            } else if (lowerTitle.includes('developer plot')) {
              score = 80 + (prop.id % 11);
            } else {
              score = 80 + (prop.id % 16);
            }
          }
          return Math.max(60, Math.min(100, Number(score)));
        };
        return getScore(b) - getScore(a);
      });
    } else if (sortBy === 'newest') {
      list.sort((a, b) => b.id - a.id);
    }
    return list;
  }, [sortBy, mappedProperties]);

  const getSortLabel = () => {
    switch (sortBy) {
      case 'price-low':
        return 'Price: Low to High';
      case 'price-high':
        return 'Price: High to Low';
      case 'score-high':
        return 'Deal Score: High to Low';
      case 'newest':
        return 'Newest First';
      default:
        return 'Default Order';
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Jost, sans-serif' }}>
      <Header />

      <div className="pt-[110px] pb-12 bg-white">
        <div className="container mx-auto px-6" style={{ maxWidth: '1480px' }}>
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-6">
            <Link href="/" className="hover:text-[#006838] transition-colors">
              Home
            </Link>
            <span>&gt;</span>
            <span className="text-slate-500 font-extrabold">{pageTitle}</span>
          </div>

          {/* Title */}
          <h1
            className="mb-6"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '30px',
              fontWeight: 500,
              lineHeight: '36px',
              color: 'rgb(80, 81, 79)',
            }}
          >
            {pageTitle}
          </h1>

          {/* Description */}
          {pageDesc && (
            <div
              className="max-w-none mb-8 prose prose-slate"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '15px',
                fontWeight: 300,
                lineHeight: '28.8px',
                color: 'rgb(80, 81, 79)',
              }}
              dangerouslySetInnerHTML={{ __html: pageDesc }}
            />
          )}

          {/* Stats and Filter Bar */}
          <div className="flex items-center justify-between border-y border-slate-200/80 py-4 mb-8">
            <span className="text-[12px] font-bold tracking-widest text-slate-500 uppercase">
              {total} Properties
            </span>
            <div className="flex items-center gap-6">
              {/* Interactive Sort Dropdown */}
              <div
                className="flex items-center gap-1 text-[12px] font-bold text-slate-500 tracking-widest uppercase relative"
                ref={dropdownRef}
              >
                <span>Sort by:</span>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="text-slate-800 font-extrabold hover:text-[#006838] transition-colors focus:outline-none flex items-center gap-1 uppercase"
                >
                  {getSortLabel()}
                  <svg
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-slate-100 shadow-xl rounded-none z-50 py-1">
                    <button
                      onClick={() => handleSortChange('default')}
                      className={`w-full text-left px-4 py-2 text-[11px] font-bold tracking-wider uppercase transition-colors hover:bg-slate-50 ${sortBy === 'default' ? 'text-[#006838]' : 'text-slate-700'}`}
                    >
                      Default Order
                    </button>
                    <button
                      onClick={() => handleSortChange('price-low')}
                      className={`w-full text-left px-4 py-2 text-[11px] font-bold tracking-wider uppercase transition-colors hover:bg-slate-50 ${sortBy === 'price-low' ? 'text-[#006838]' : 'text-slate-700'}`}
                    >
                      Price: Low to High
                    </button>
                    <button
                      onClick={() => handleSortChange('price-high')}
                      className={`w-full text-left px-4 py-2 text-[11px] font-bold tracking-wider uppercase transition-colors hover:bg-slate-50 ${sortBy === 'price-high' ? 'text-[#006838]' : 'text-slate-700'}`}
                    >
                      Price: High to Low
                    </button>
                    <button
                      onClick={() => handleSortChange('score-high')}
                      className={`w-full text-left px-4 py-2 text-[11px] font-bold tracking-wider uppercase transition-colors hover:bg-slate-50 ${sortBy === 'score-high' ? 'text-[#006838]' : 'text-slate-700'}`}
                    >
                      Deal Score: High to Low
                    </button>
                    <button
                      onClick={() => handleSortChange('newest')}
                      className={`w-full text-left px-4 py-2 text-[11px] font-bold tracking-wider uppercase transition-colors hover:bg-slate-50 ${sortBy === 'newest' ? 'text-[#006838]' : 'text-slate-700'}`}
                    >
                      Newest First
                    </button>
                  </div>
                )}
              </div>

              {/* View Toggle Mode Buttons */}
              <div className="flex items-center gap-2 text-slate-400">
                <button
                  onClick={() => handleViewChange('list')}
                  className={`p-1 hover:text-[#006838] transition-colors ${viewMode === 'list' ? 'text-[#006838]' : 'text-slate-400'}`}
                  title="List View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleViewChange('grid')}
                  className={`p-1 hover:text-[#006838] transition-colors ${viewMode === 'grid' ? 'text-[#006838]' : 'text-slate-400'}`}
                  title="Grid View"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h8M4 12h8M4 18h8M16 6h4M16 12h4M16 18h4"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Grid/List Dynamic Content */}
          {sortedProperties.length > 0 ? (
            <>
              <div
                className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8'
                    : 'flex flex-col gap-6 w-full'
                }
              >
                {sortedProperties.map((mappedProperty: Property) => (
                  <PropertyCard
                    key={mappedProperty.id}
                    property={mappedProperty}
                    viewMode={viewMode}
                    onQuickView={setQuickViewProperty}
                  />
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-16 border-t border-slate-100 pt-8">
                  {currentPage > 1 && (
                    <Link
                      href={getPageHref(currentPage - 1)}
                      className="w-12 h-12 flex items-center justify-center border border-slate-200 bg-white text-slate-700 hover:bg-[#006838] hover:border-[#006838] hover:text-white transition-all duration-150 rounded-none"
                    >
                      <svg
                        className="w-4 h-4 transform rotate-180"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  )}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <Link
                      key={pageNum}
                      href={getPageHref(pageNum)}
                      className={`w-12 h-12 flex items-center justify-center border transition-all duration-150 rounded-none ${
                        pageNum === currentPage
                          ? 'bg-[#006838] border-[#006838] text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-[#006838] hover:border-[#006838] hover:text-white'
                      }`}
                    >
                      {pageNum}
                    </Link>
                  ))}
                  {currentPage < totalPages && (
                    <Link
                      href={getPageHref(currentPage + 1)}
                      className="w-12 h-12 flex items-center justify-center border border-slate-200 bg-white text-slate-700 hover:bg-[#006838] hover:border-[#006838] hover:text-white transition-all duration-150 rounded-none"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No properties found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick View Modal Overlay */}
      {quickViewProperty && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 md:p-6"
          onClick={() => setQuickViewProperty(null)}
        >
          <div
            ref={modalContainerRef}
            className="relative bg-white w-full max-w-5xl rounded-none overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#2a2b2a] text-white px-5 py-3.5 flex items-center justify-between select-none">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-[#82BC41]" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#82BC41]">
                  Plot Details
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuickViewProperty(null)}
                  className="hover:text-[#82BC41] transition-colors text-[16px] font-black leading-none"
                  title="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex flex-col md:flex-row w-full bg-white h-auto md:h-[500px]">
              {/* Left Column (Image) */}
              <div className="w-full md:w-[60%] h-64 md:h-full relative bg-slate-50 flex-shrink-0">
                <AppImage
                  src={quickViewProperty.image}
                  alt={quickViewProperty.alt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 60vw"
                />
              </div>

              {/* Right Column (Details) */}
              <div className="w-full md:w-[40%] p-6 md:p-8 flex flex-col justify-between overflow-hidden">
                <div>
                  <div className="flex justify-between items-start gap-4 mb-4">
                    <h3
                      className="font-bold text-slate-800 text-[18px] md:text-[20px] leading-snug line-clamp-2"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {quickViewProperty.title}
                    </h3>
                    <Link
                      href={quickViewProperty.href}
                      className="text-slate-400 hover:text-[#006838] transition-colors p-1"
                      title="Expand View"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                        />
                      </svg>
                    </Link>
                  </div>

                  <div className="text-[20px] md:text-[22px] font-black text-[#006838] mb-6">
                    {formatPrice(quickViewProperty.price)}
                  </div>


                  {/* Metadata Specs Grid */}
                  <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-6 mb-6">
                    {quickViewProperty.land_area && (
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                          <svg
                            className="w-3.5 h-3.5 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M9 4v16M15 4v16M4 9h16M4 15h16"
                            />
                          </svg>
                          Plot Area
                        </span>
                        <span className="text-[13px] font-extrabold text-slate-700">
                          {formatAreaValue(quickViewProperty.land_area)} sq. ft.
                        </span>
                      </div>
                    )}
                    {quickViewProperty.gfa && (
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                          <svg
                            className="w-3.5 h-3.5 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                          Built Up Area
                        </span>
                        <span className="text-[13px] font-extrabold text-slate-700">
                          {formatAreaValue(quickViewProperty.gfa)} sq. ft.
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <Link
                  href={quickViewProperty.href}
                  className="w-full bg-[#1d70b8] hover:bg-[#155a96] text-white font-extrabold uppercase text-[11px] tracking-widest py-3.5 text-center transition-all duration-150 shadow-md hover:shadow-lg mt-4"
                >
                  Details
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
